import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Clock, Sparkles } from "lucide-react";
import { Seo } from "@/seo/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { StartBuild } from "@/components/site/primitives";
import { INSIGHT_BY_SLUG, relatedInsights } from "@/data/insights";
import { LANDING_BY_SLUG } from "@/data/serviceLandings";
import { articleSchema, faqSchema, breadcrumbSchema } from "@/seo/config";
import { trackEvent } from "@/lib/attribution";
import { insightView } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.65, ease },
};
const CONTAINER = "max-w-[1400px] mx-auto px-6 md:px-12";
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function Article() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const a = INSIGHT_BY_SLUG[slug];

  useEffect(() => {
    if (a) {
      trackEvent("article_view", { slug: a.slug, category: a.category }, { once: `article:${a.slug}` });
      insightView(a.slug);
    }
  }, [a]);

  if (!a) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <Seo title="Article not found | AIROTECH" path={pathname} noindex />
        <h1 className="font-heading text-4xl text-white mb-4">Article not found</h1>
        <Link to="/insights" className="mono-label text-cyan-300">View all insights →</Link>
      </div>
    );
  }

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Insights", to: "/insights" },
    { label: a.title, to: `/insights/${a.slug}` },
  ];
  const related = relatedInsights(a.slug, 3);
  const services = (a.relatedServices || []).map((s) => LANDING_BY_SLUG[s]).filter(Boolean);

  return (
    <div>
      <Seo
        title={a.title + " | AIROTECH"}
        description={a.description}
        path={`/insights/${a.slug}`}
        type="article"
        schema={[articleSchema(a), faqSchema(a.faqs), breadcrumbSchema(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      {/* Header */}
      <article>
        <header className="pt-10 md:pt-14 pb-10 md:pb-14 border-b border-[#141414]">
          <div className={`${CONTAINER} max-w-4xl mx-auto`}>
            <motion.div {...reveal} className="flex items-center gap-3 mono-label text-cyan-400/80">
              <span>{a.category}</span>
              {a.kind === "pillar" && (
                <span className="rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] px-2.5 py-0.5 text-[10px] text-cyan-300">
                  PILLAR GUIDE
                </span>
              )}
            </motion.div>
            <motion.h1
              {...reveal}
              className="mt-6 font-heading font-extrabold tracking-tighter leading-[1.02] text-4xl md:text-5xl lg:text-[3.4rem]"
            >
              {a.title}
            </motion.h1>
            <motion.div {...reveal} className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 mono-label text-zinc-500">
              <span className="text-zinc-300">{a.author}</span>
              <span>Published {fmtDate(a.publishDate)}</span>
              {a.updatedDate && a.updatedDate !== a.publishDate && (
                <span>Updated {fmtDate(a.updatedDate)}</span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {a.readMins} min read
              </span>
            </motion.div>
          </div>
        </header>

        {/* Quick answer (AEO) */}
        <section className="py-10 md:py-14">
          <div className={`${CONTAINER} max-w-3xl mx-auto`}>
            <motion.div
              {...reveal}
              data-testid="article-quick-answer"
              className="rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-7 md:p-8"
            >
              <div className="flex items-center gap-2 mono-label text-cyan-300 mb-4">
                <Sparkles className="h-4 w-4" /> THE SHORT ANSWER
              </div>
              <p className="text-lg text-zinc-200 leading-relaxed">{a.answer}</p>
            </motion.div>
          </div>
        </section>

        {/* Body */}
        <section className="pb-8">
          <div className={`${CONTAINER} max-w-3xl mx-auto`}>
            {a.sections.map((s) => (
              <motion.div key={s.h} {...reveal} className="mb-12">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-white tracking-tight">{s.h}</h2>
                {(s.p || []).map((para, i) => (
                  <p key={i} className="mt-5 text-zinc-300 text-[1.06rem] leading-[1.85]">{para}</p>
                ))}
                {s.list && (
                  <ul className="mt-6 space-y-3">
                    {s.list.map((li, i) => (
                      <li key={i} className="flex items-start gap-3 text-zinc-300 leading-relaxed">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.sub && (
                  <div className="mt-8 space-y-7 border-l border-[#1c1c1c] pl-6">
                    {s.sub.map((ss) => (
                      <div key={ss.h}>
                        <h3 className="font-heading text-lg text-white">{ss.h}</h3>
                        {(ss.p || []).map((para, i) => (
                          <p key={i} className="mt-2.5 text-zinc-400 leading-relaxed">{para}</p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        {a.faqs?.length > 0 && (
          <section className="py-12 border-t border-[#131313]">
            <div className={`${CONTAINER} max-w-3xl mx-auto`}>
              <motion.h2 {...reveal} className="font-heading text-2xl md:text-3xl text-white tracking-tight mb-8">
                Frequently asked questions
              </motion.h2>
              {a.faqs.map((f) => (
                <motion.details key={f.q} {...reveal} className="group border-b border-[#161616] py-6">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                    <span className="font-heading text-lg text-white">{f.q}</span>
                    <Plus className="h-5 w-5 shrink-0 text-cyan-400 transition-transform duration-300 group-open:rotate-45" />
                  </summary>
                  <p className="mt-4 text-zinc-400 leading-relaxed">{f.a}</p>
                </motion.details>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* Related services (internal linking) */}
      {services.length > 0 && (
        <section className="py-14 border-t border-[#131313]">
          <div className={`${CONTAINER} max-w-3xl mx-auto`}>
            <div className="mono-label text-zinc-600 mb-6">Related services</div>
            <div className="flex flex-wrap gap-3">
              {services.map((rp) => (
                <Link
                  key={rp.slug}
                  to={`/${rp.slug}`}
                  data-testid={`article-service-${rp.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-[#232323] px-5 py-2.5 mono-label text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors"
                >
                  {rp.nav}
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Continue reading */}
      {related.length > 0 && (
        <section className="py-14 border-t border-[#131313]">
          <div className={CONTAINER}>
            <div className="mono-label text-zinc-600 mb-8">Continue reading</div>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/insights/${r.slug}`}
                  data-testid={`article-related-${r.slug}`}
                  className="group rounded-2xl border border-[#191919] bg-[#0a0a0a] p-7 hover:border-cyan-400/40 transition-colors"
                >
                  <div className="mono-label text-cyan-400/70">{r.category}</div>
                  <h3 className="mt-3 font-heading font-bold text-lg text-white group-hover:text-cyan-100 transition-colors">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-500 leading-relaxed line-clamp-3">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 border-t border-[#131313]">
        <div className={CONTAINER}>
          <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-10 md:p-16 text-center">
            <h2 className="font-serif-ed text-white text-4xl md:text-5xl leading-[1.05]">Ready to build it?</h2>
            <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-lg">
              Start a guided product journey. Tell us what you want to build — we'll take it from there.
            </p>
            <div className="mt-9 flex justify-center">
              <StartBuild testid="article-cta-start-build-btn" size="lg" label="Start Your Build" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
